import { useRoomContext } from "@livekit/components-react"
import { Track } from "livekit-client"
import { useEffect, useRef } from "react"

/**
 * LiveKit Stats Monitor - Development tool for monitoring bandwidth consumption
 * Logs readable stats to the browser console every 3 seconds
 *
 * Usage: Add <LiveKitStatsMonitor /> inside LiveKitRoom when __DEV__ is true
 */
const LiveKitStatsMonitor = () => {
    const room = useRoomContext()
    const intervalRef = useRef(null)
    const previousBytesRef = useRef({})

    useEffect(() => {
        if (!room) return

        const getTrackStats = async () => {
            const stats = {
                local: { camera: null, screen: null, audio: null },
                remote: [],
                timestamp: new Date().toLocaleTimeString(),
            }

            // Get local participant stats
            const localParticipant = room.localParticipant
            if (localParticipant) {
                for (const [, publication] of localParticipant.trackPublications) {
                    if (!publication.track) continue

                    const trackStats = await getTrackBitrate(publication.track, publication.source, true)

                    if (publication.source === Track.Source.Camera) {
                        stats.local.camera = trackStats
                    } else if (publication.source === Track.Source.ScreenShare) {
                        stats.local.screen = trackStats
                    } else if (publication.source === Track.Source.Microphone) {
                        stats.local.audio = trackStats
                    }
                }
            }

            // Get remote participants stats
            for (const [, participant] of room.remoteParticipants) {
                const participantStats = {
                    name: participant.identity?.split(".")[1] || participant.identity,
                    camera: null,
                    screen: null,
                    audio: null,
                }

                for (const [, publication] of participant.trackPublications) {
                    if (!publication.track || !publication.isSubscribed) continue

                    const trackStats = await getTrackBitrate(publication.track, publication.source, false)

                    if (publication.source === Track.Source.Camera) {
                        participantStats.camera = trackStats
                    } else if (publication.source === Track.Source.ScreenShare) {
                        participantStats.screen = trackStats
                    } else if (publication.source === Track.Source.Microphone) {
                        participantStats.audio = trackStats
                    }
                }

                stats.remote.push(participantStats)
            }

            logStats(stats)
        }

        const getTrackBitrate = async (track, source, isLocal) => {
            try {
                // Get WebRTC stats
                const statsReport = await track.getRTCStatsReport()
                if (!statsReport) return null

                let bytesNow = 0
                let resolution = null
                let frameRate = null
                let packetsLost = 0
                let packetsTotal = 0
                let codec = null

                for (const [, report] of statsReport) {
                    // Get bytes sent/received
                    if (report.type === "outbound-rtp" && isLocal) {
                        bytesNow = report.bytesSent || 0
                        packetsLost = report.packetsLost || 0
                        packetsTotal = report.packetsSent || 0

                        if (report.frameWidth && report.frameHeight) {
                            resolution = `${report.frameWidth}x${report.frameHeight}`
                        }
                        if (report.framesPerSecond) {
                            frameRate = Math.round(report.framesPerSecond)
                        }
                    }

                    if (report.type === "inbound-rtp" && !isLocal) {
                        bytesNow = report.bytesReceived || 0
                        packetsLost = report.packetsLost || 0
                        packetsTotal = report.packetsReceived || 0

                        if (report.frameWidth && report.frameHeight) {
                            resolution = `${report.frameWidth}x${report.frameHeight}`
                        }
                        if (report.framesPerSecond) {
                            frameRate = Math.round(report.framesPerSecond)
                        }
                    }

                    // Get codec info
                    if (report.type === "codec") {
                        codec = report.mimeType?.split("/")[1]?.toUpperCase()
                    }
                }

                // Calculate bitrate (bytes diff over 3 seconds interval)
                const trackKey = `${track.sid}_${isLocal ? "out" : "in"}`
                const previousBytes = previousBytesRef.current[trackKey] || 0
                const bytesDiff = bytesNow - previousBytes
                previousBytesRef.current[trackKey] = bytesNow

                // Convert to Mbps (bytes * 8 / 3 seconds / 1000000)
                const bitrateMbps = (bytesDiff * 8 / 3 / 1000000).toFixed(2)
                const bitrateKbps = Math.round(bytesDiff * 8 / 3 / 1000)

                // Calculate packet loss percentage
                const packetLossPercent = packetsTotal > 0
                    ? ((packetsLost / packetsTotal) * 100).toFixed(1)
                    : "0.0"

                return {
                    bitrateMbps: parseFloat(bitrateMbps),
                    bitrateKbps,
                    resolution,
                    frameRate,
                    packetLossPercent: parseFloat(packetLossPercent),
                    codec,
                    isVideo: source === Track.Source.Camera || source === Track.Source.ScreenShare,
                }
            } catch (e) {
                return null
            }
        }

        const logStats = (stats) => {
            const lines = []
            let totalUpload = 0
            let totalDownload = 0

            lines.push(`\n📊 LiveKit Stats [${stats.timestamp}]`)
            lines.push("═".repeat(50))

            // Local stats
            lines.push("\n📤 UPLOAD (Local)")
            lines.push("-".repeat(30))

            if (stats.local.camera) {
                const cam = stats.local.camera
                totalUpload += cam.bitrateMbps
                const warning = cam.resolution && parseInt(cam.resolution.split("x")[0]) > 1280 ? " ⚠️ HIGH RES" : ""
                lines.push(`📹 Camera: ${cam.resolution || "N/A"} @ ${cam.frameRate || "?"}fps — ${cam.bitrateMbps} Mbps${warning}`)
                if (cam.packetLossPercent > 2) {
                    lines.push(`   ⚠️ Packet loss: ${cam.packetLossPercent}%`)
                }
            } else {
                lines.push("📹 Camera: OFF")
            }

            if (stats.local.screen) {
                const scr = stats.local.screen
                totalUpload += scr.bitrateMbps
                const warning = scr.resolution && parseInt(scr.resolution.split("x")[0]) > 1920 ? " ⚠️ HIGH RES" : ""
                lines.push(`🖥️  Screen: ${scr.resolution || "N/A"} @ ${scr.frameRate || "?"}fps — ${scr.bitrateMbps} Mbps${warning}`)
                if (scr.packetLossPercent > 2) {
                    lines.push(`   ⚠️ Packet loss: ${scr.packetLossPercent}%`)
                }
            } else {
                lines.push("🖥️  Screen: OFF")
            }

            if (stats.local.audio) {
                const aud = stats.local.audio
                totalUpload += aud.bitrateMbps
                lines.push(`🎤 Audio: ${aud.bitrateKbps} kbps`)
            } else {
                lines.push("🎤 Audio: OFF")
            }

            // Remote stats
            if (stats.remote.length > 0) {
                lines.push("\n📥 DOWNLOAD (Remote)")
                lines.push("-".repeat(30))

                for (const participant of stats.remote) {
                    lines.push(`\n👤 ${participant.name}`)

                    if (participant.camera) {
                        const cam = participant.camera
                        totalDownload += cam.bitrateMbps
                        lines.push(`   📹 Camera: ${cam.resolution || "N/A"} @ ${cam.frameRate || "?"}fps — ${cam.bitrateMbps} Mbps`)
                        if (cam.packetLossPercent > 2) {
                            lines.push(`      ⚠️ Packet loss: ${cam.packetLossPercent}%`)
                        }
                    }

                    if (participant.screen) {
                        const scr = participant.screen
                        totalDownload += scr.bitrateMbps
                        lines.push(`   🖥️  Screen: ${scr.resolution || "N/A"} @ ${scr.frameRate || "?"}fps — ${scr.bitrateMbps} Mbps`)
                    }

                    if (participant.audio) {
                        const aud = participant.audio
                        totalDownload += aud.bitrateMbps
                        lines.push(`   🎤 Audio: ${aud.bitrateKbps} kbps`)
                    }
                }
            }

            // Summary
            lines.push("\n" + "═".repeat(50))
            lines.push(`📊 TOTAL: ↑ ${totalUpload.toFixed(2)} Mbps | ↓ ${totalDownload.toFixed(2)} Mbps`)
            lines.push(`🌐 Connection: ${room.connectionState}`)
            lines.push("═".repeat(50))

            console.log(lines.join("\n"))
        }

        // Start monitoring after a short delay to let tracks initialize
        const startTimeout = setTimeout(() => {
            getTrackStats() // Initial call
            intervalRef.current = setInterval(getTrackStats, 3000) // Every 3 seconds
        }, 2000)

        return () => {
            clearTimeout(startTimeout)
            if (intervalRef.current) {
                clearInterval(intervalRef.current)
            }
        }
    }, [room])

    // This component doesn't render anything
    return null
}

export default LiveKitStatsMonitor
